import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { DistributionFields, EstimationPanel, REVIEW_LANGUAGES, DEFAULT_FROM, todayISO } from '../components/GenerationControls';
import GenerationLog from '../components/GenerationLog';
import AutoGenSettings from '../components/AutoGenSettings';
import { Building2, MessageSquare, LogOut, ExternalLink, Layers, X, RefreshCw, Check, Sparkles, CalendarClock, AlertTriangle, RotateCw, Languages } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllModal, setShowAllModal] = useState(false);
  const [allMode, setAllMode] = useState('random');
  const [allTarget, setAllTarget] = useState('1200');
  const [randMin, setRandMin] = useState('1150');
  const [randMax, setRandMax] = useState('1260');
  const [allJob, setAllJob] = useState(null);
  const [allStarting, setAllStarting] = useState(false);
  const [allError, setAllError] = useState('');
  const [allRating, setAllRating] = useState('excellent');
  const [allFrom, setAllFrom] = useState(DEFAULT_FROM);
  const [allTo, setAllTo] = useState(todayISO());
  // Génération par entreprise
  const [oneCompany, setOneCompany] = useState(null);
  const [oneTarget, setOneTarget] = useState('');
  const [oneCurrent, setOneCurrent] = useState(0);
  const [oneJob, setOneJob] = useState(null);
  const [oneStarting, setOneStarting] = useState(false);
  const [oneError, setOneError] = useState('');
  const [oneRating, setOneRating] = useState('excellent');
  const [oneFrom, setOneFrom] = useState(DEFAULT_FROM);
  const [oneTo, setOneTo] = useState(todayISO());
  const [onePreview, setOnePreview] = useState(null);
  const [onePreviewLoading, setOnePreviewLoading] = useState(false);
  const [logRefresh, setLogRefresh] = useState(0);
  const [showAutoGen, setShowAutoGen] = useState(false);
  const [alerts, setAlerts] = useState({ resuming: [], interrupted: [] });
  const [dismissed, setDismissed] = useState([]);

  const loadAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/alerts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setAlerts(await res.json());
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    loadAlerts();
    const id = setInterval(loadAlerts, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const relaunchInterrupted = (domain) => {
    setDismissed((d) => [...d, domain]);
    if (domain === '__all__') { openAllModal(); return; }
    const company = companies.find((c) => c.domain === domain);
    if (company) openOneModal(company);
  };

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    checkAuth();
    loadCompanies();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (err) {
      navigate('/admin/login');
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/companies`);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const updateLanguage = async (domain, lang) => {
    setCompanies((prev) => prev.map((c) => (c.domain === domain ? { ...c, review_language: lang } : c)));
    try {
      await fetch(`${API_URL}/api/admin/reviews/company/${domain}/language`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ review_language: lang }),
      });
    } catch (err) { /* ignore */ }
  };

  const openAllModal = async () => {
    setShowAllModal(true);
    setAllError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/bulk-status-all`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllJob(data.overall?.status === 'running' ? data : { overall: { status: 'idle' }, companies: data.companies });
      }
    } catch (err) {
      // ignore
    }
  };

  const startAll = async () => {
    const token = getToken();
    setAllError('');
    let body;
    if (allMode === 'random') {
      const mn = parseInt(randMin, 10), mx = parseInt(randMax, 10);
      if (!mn || !mx || mn > mx) { setAllError('Min et Max invalides (min ≤ max).'); return; }
      body = { mode: 'random', random_min: mn, random_max: mx };
    } else {
      const t = parseInt(allTarget, 10);
      if (!t) { setAllError('Cible invalide.'); return; }
      body = { mode: 'target', target: t };
    }
    body.rating_preset = allRating;
    body.date_from = allFrom;
    body.date_to = allTo;
    setAllStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/bulk-generate-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        setAllJob((prev) => ({ overall: { status: 'running', companies_done: 0, total_companies: data.total_companies }, companies: prev?.companies || [] }));
      } else if (res.status === 401) {
        navigate('/admin/login');
      } else {
        setAllError(data.detail || 'Erreur lors du démarrage');
      }
    } catch (err) {
      setAllError('Erreur de connexion');
    } finally {
      setAllStarting(false);
    }
  };

  useEffect(() => {
    if (!showAllModal || allJob?.overall?.status !== 'running') return;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/reviews/bulk-status-all`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAllJob(data);
          if (data.overall?.status !== 'running') {
            clearInterval(id);
            loadCompanies();
            setLogRefresh((k) => k + 1);
          }
        }
      } catch (err) {
        // ignore
      }
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllModal, allJob?.overall?.status]);

  const previewOne = async () => {
    if (!oneCompany) return;
    setOnePreviewLoading(true);
    setOnePreview(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/preview/${oneCompany.domain}?rating_preset=${oneRating}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setOnePreview(await res.json());
      else setOneError("Impossible de générer l'aperçu.");
    } catch (err) {
      setOneError("Impossible de générer l'aperçu.");
    } finally {
      setOnePreviewLoading(false);
    }
  };

  // ---- Génération par entreprise ----
  const openOneModal = async (company) => {
    setOneCompany(company);
    setOneError('');
    setOnePreview(null);
    const cur = company.stats?.total_reviews || 0;
    setOneCurrent(cur);
    setOneTarget(String(cur + 100));
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/bulk-status/${company.domain}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const j = await res.json();
        setOneJob(j.status === 'running' ? j : null);
      } else {
        setOneJob(null);
      }
    } catch (err) {
      setOneJob(null);
    }
  };

  const startOne = async () => {
    const token = getToken();
    const target = parseInt(oneTarget, 10);
    if (!token || !oneCompany || !target) return;
    if (target <= oneCurrent) {
      setOneError(`La cible doit être supérieure au total actuel (${oneCurrent}).`);
      return;
    }
    setOneStarting(true);
    setOneError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/bulk-generate/${oneCompany.domain}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ target, rating_preset: oneRating, date_from: oneFrom, date_to: oneTo })
      });
      const data = await res.json();
      if (res.ok) {
        setOneJob({ status: 'running', created: 0, to_create: data.to_create, target, current_total: oneCurrent });
      } else if (res.status === 401) {
        navigate('/admin/login');
      } else {
        setOneError(data.detail || 'Erreur lors du démarrage');
      }
    } catch (err) {
      setOneError('Erreur de connexion');
    } finally {
      setOneStarting(false);
    }
  };

  useEffect(() => {
    if (!oneCompany || oneJob?.status !== 'running') return;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/reviews/bulk-status/${oneCompany.domain}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (res.ok) {
          const j = await res.json();
          setOneJob(j);
          if (j.status !== 'running') {
            clearInterval(id);
            loadCompanies();
            setLogRefresh((k) => k + 1);
          }
        }
      } catch (err) {
        // ignore
      }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneCompany, oneJob?.status]);

  const stopOne = async () => {
    if (!oneCompany) return;
    try {
      await fetch(`${API_URL}/api/admin/reviews/bulk-stop/${oneCompany.domain}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }
      });
    } catch (err) { /* ignore */ }
  };

  const stopAll = async () => {
    try {
      await fetch(`${API_URL}/api/admin/reviews/bulk-stop-all`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }
      });
    } catch (err) { /* ignore */ }
  };

  // Nombre d'avis à créer pour la génération multi-entreprises (selon le mode)
  const allEstimateCount = () => {
    const totals = companies.map((c) => c.stats?.total_reviews || 0);
    if (allMode === 'random') {
      const mn = parseInt(randMin, 10), mx = parseInt(randMax, 10);
      if (!mn || !mx) return 0;
      const lo = totals.reduce((s, cur) => s + Math.max(0, mn - cur), 0);
      const hi = totals.reduce((s, cur) => s + Math.max(0, mx - cur), 0);
      return [lo, hi];
    }
    const t = parseInt(allTarget, 10);
    if (!t) return 0;
    return totals.reduce((s, cur) => s + Math.max(0, t - cur), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00b67a]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a1a]">Administration FairViews</h1>
            <p className="text-gray-600">Gérez les avis de vos entreprises</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAutoGen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-[#00b67a] text-[#00b67a] rounded-lg hover:bg-[#00b67a]/10 transition-colors text-sm font-medium"
              data-testid="autogen-open-button"
            >
              <CalendarClock className="w-4 h-4" />
              Génération auto
            </button>
            <button
              onClick={openAllModal}
              className="flex items-center gap-2 px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] transition-colors text-sm font-medium"
              data-testid="bulk-all-button"
            >
              <Layers className="w-4 h-4" />
              Génération en masse (toutes)
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>

        {/* Bandeau d'alerte : reprises + jobs interrompus */}
        {alerts.resuming.length > 0 && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="alert-resuming">
            <div className="flex items-start gap-3">
              <RotateCw className="w-5 h-5 text-blue-600 mt-0.5 animate-spin" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">Reprise automatique en cours</p>
                <p className="text-sm text-blue-700 mt-0.5">
                  Une génération interrompue par un redémarrage a repris là où elle s'était arrêtée :
                </p>
                <ul className="text-sm text-blue-800 mt-1 space-y-0.5">
                  {alerts.resuming.map((r) => (
                    <li key={r.domain} data-testid={`resuming-${r.domain}`}>
                      • <strong>{r.name}</strong>
                      {r.domain === '__all__'
                        ? ` — ${r.companies_done || 0}/${r.total_companies || 0} entreprises`
                        : ` — ${r.created || 0}/${r.to_create || 0} avis (cible ${r.target})`}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {alerts.interrupted.filter((i) => !dismissed.includes(i.domain)).length > 0 && (
          <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4" data-testid="alert-interrupted">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-orange-900">Génération interrompue par un redémarrage</p>
                <p className="text-sm text-orange-700 mt-0.5">
                  Ces générations ont été stoppées par un redémarrage du serveur. Vous pouvez les relancer (elles reprendront depuis le total actuel).
                </p>
                <ul className="mt-2 space-y-2">
                  {alerts.interrupted.filter((i) => !dismissed.includes(i.domain)).map((i) => (
                    <li key={i.domain} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-orange-100 px-3 py-2" data-testid={`interrupted-${i.domain}`}>
                      <span className="text-sm text-[#1a1a1a]">
                        <strong>{i.name}</strong>
                        {i.target ? <span className="text-gray-500"> — total actuel {i.current_total ?? '—'} / cible {i.target}</span> : null}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => relaunchInterrupted(i.domain)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] transition-colors text-sm font-medium"
                          data-testid={`relaunch-${i.domain}`}
                        >
                          <RotateCw className="w-4 h-4" /> Reprendre
                        </button>
                        <button
                          onClick={() => setDismissed((d) => [...d, i.domain])}
                          className="text-orange-400 hover:text-orange-600"
                          title="Ignorer"
                          data-testid={`dismiss-${i.domain}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Companies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => (
            <div key={company.id} className="bg-white rounded-lg border p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-[#00b67a] rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {company.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#1a1a1a]">{company.name}</h3>
                  <p className="text-sm text-gray-500">{company.domain}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" />
                  {company.stats?.total_reviews || 0} avis
                </span>
                <span>Note: {company.stats?.trust_score || 0}/5</span>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-1 text-xs font-medium text-gray-500 mb-1">
                  <Languages className="w-3.5 h-3.5" /> Langue des avis générés
                </label>
                <select
                  value={company.review_language || 'fr'}
                  onChange={(e) => updateLanguage(company.domain, e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
                  data-testid={`language-select-${company.domain}`}
                >
                  {REVIEW_LANGUAGES.map((l) => (
                    <option key={l.value} value={l.value}>{l.flag} {l.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2">
                <Link
                  to={`/admin/reviews/${company.domain}`}
                  className="flex-1 text-center py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] transition-colors text-sm font-medium"
                >
                  Modérer les avis
                </Link>
                <button
                  onClick={() => openOneModal(company)}
                  className="p-2 border border-[#00b67a] rounded-lg hover:bg-[#00b67a]/10 transition-colors"
                  title="Générer des avis pour cette entreprise"
                  data-testid={`generate-company-${company.domain}`}
                >
                  <Sparkles className="w-5 h-5 text-[#00b67a]" />
                </button>
                <Link
                  to={`/review/${company.domain}`}
                  className="p-2 border rounded-lg hover:bg-gray-50 transition-colors"
                  title="Voir la page"
                >
                  <ExternalLink className="w-5 h-5 text-gray-500" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <GenerationLog refreshKey={logRefresh} />
      </main>

      {showAutoGen && <AutoGenSettings onClose={() => setShowAutoGen(false)} />}

      {/* Bulk Generate All Modal */}
      {showAllModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="bulk-all-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
                  <Layers className="w-5 h-5 text-[#00b67a]" />
                  Génération en masse — toutes les entreprises
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Traite chaque entreprise l'une après l'autre. Les entreprises déjà au-dessus de la cible sont ignorées.
                </p>
              </div>
              <button onClick={() => setShowAllModal(false)} className="text-gray-400 hover:text-gray-600" data-testid="bulk-all-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {allJob?.overall?.status === 'running' ? (
              <div data-testid="bulk-all-progress">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600 flex items-center gap-1">
                    <RefreshCw className="w-4 h-4 animate-spin" /> En cours…
                  </span>
                  <span className="font-medium text-[#1a1a1a]">
                    {allJob.overall.companies_done || 0} / {allJob.overall.total_companies || 0} entreprises
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden mb-4">
                  <div className="bg-[#00b67a] h-3 rounded-full transition-all duration-500"
                    style={{ width: `${allJob.overall.total_companies ? Math.round(((allJob.overall.companies_done || 0) / allJob.overall.total_companies) * 100) : 0}%` }} />
                </div>
                <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                  {(allJob.companies || []).map((c) => (
                    <div key={c.domain} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-[#1a1a1a]">{c.name}</span>
                      <span className={`text-xs font-medium ${c.job_status === 'done' ? 'text-[#00b67a]' : c.job_status === 'running' ? 'text-blue-600' : 'text-gray-400'}`}>
                        {c.job_status === 'running' ? `${c.created}/${c.to_create}…` : c.job_status === 'done' ? `✓ ${c.total_reviews} avis` : c.job_status}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">Tu peux fermer cette fenêtre, la génération continue côté serveur.</p>
                <div className="flex justify-center mt-4">
                  <button onClick={stopAll} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium" data-testid="bulk-all-stop">
                    <X className="w-4 h-4" /> Arrêter la génération
                  </button>
                </div>
              </div>
            ) : (allJob?.overall?.status === 'done' || allJob?.overall?.status === 'stopped') ? (
              <div className="text-center py-4" data-testid="bulk-all-done">
                <Check className="w-10 h-10 text-[#00b67a] mx-auto mb-2" />
                <p className="font-medium text-[#1a1a1a]">{allJob.overall.status === 'stopped' ? 'Génération arrêtée' : 'Terminé pour toutes les entreprises !'}</p>
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto mt-4 text-left">
                  {(allJob.companies || []).map((c) => (
                    <div key={c.domain} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-[#1a1a1a]">{c.name}</span>
                      <span className="text-xs font-medium text-[#00b67a]">{c.total_reviews} avis</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setAllJob({ overall: { status: 'idle' }, companies: allJob.companies })} className="mt-4 text-sm text-[#00b67a] hover:underline" data-testid="bulk-all-again">
                  Relancer
                </button>
              </div>
            ) : (
              <div>
                <div className="flex gap-2 mb-4" data-testid="bulk-all-mode">
                  <button onClick={() => setAllMode('random')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${allMode === 'random' ? 'bg-[#00b67a] text-white' : 'bg-gray-100 text-gray-700'}`} data-testid="mode-random">
                    Cible aléatoire (recommandé)
                  </button>
                  <button onClick={() => setAllMode('target')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${allMode === 'target' ? 'bg-[#00b67a] text-white' : 'bg-gray-100 text-gray-700'}`} data-testid="mode-target">
                    Cible fixe
                  </button>
                </div>

                {allMode === 'random' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Minimum</label>
                      <input type="number" value={randMin} onChange={(e) => setRandMin(e.target.value)} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]" data-testid="rand-min-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Maximum</label>
                      <input type="number" value={randMax} onChange={(e) => setRandMax(e.target.value)} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]" data-testid="rand-max-input" />
                    </div>
                    <p className="col-span-2 text-xs text-gray-500">Chaque entreprise sera amenée à un total aléatoire tiré entre ces deux valeurs.</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre total d'avis par entreprise</label>
                    <input type="number" value={allTarget} onChange={(e) => setAllTarget(e.target.value)} className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]" data-testid="all-target-input" />
                    <p className="text-xs text-gray-500 mt-1">Chaque entreprise sera amenée à ce total (celles déjà au-dessus sont ignorées).</p>
                  </div>
                )}

                <DistributionFields
                  ratingPreset={allRating} setRatingPreset={setAllRating}
                  dateFrom={allFrom} setDateFrom={setAllFrom}
                  dateTo={allTo} setDateTo={setAllTo}
                  idPrefix="all-"
                />

                <EstimationPanel count={allEstimateCount()} idPrefix="all-" />

                {allError && <p className="text-sm text-red-600 mt-3" data-testid="bulk-all-error">{allError}</p>}

                <div className="flex justify-end gap-2 mt-6">
                  <button onClick={() => setShowAllModal(false)} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 transition-colors" data-testid="bulk-all-cancel">Annuler</button>
                  <button onClick={startAll} disabled={allStarting} className="flex items-center gap-2 px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] disabled:opacity-50 transition-colors font-medium" data-testid="bulk-all-start">
                    <Layers className="w-4 h-4" />
                    {allStarting ? 'Démarrage...' : 'Lancer sur toutes'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Per-company Generate Modal */}
      {oneCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="generate-one-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#00b67a]" />
                  Générer des avis — {oneCompany.name}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Total actuel : <strong>{oneCurrent}</strong> avis. Dates étalées, notes ~4.8, publiés automatiquement.
                </p>
              </div>
              <button onClick={() => { setOneCompany(null); setOneJob(null); }} className="text-gray-400 hover:text-gray-600" data-testid="generate-one-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {oneJob?.status === 'running' ? (
              <div data-testid="generate-one-progress">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600 flex items-center gap-1"><RefreshCw className="w-4 h-4 animate-spin" /> Génération en cours…</span>
                  <span className="font-medium text-[#1a1a1a]">{oneJob.created || 0} / {oneJob.to_create || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div className="bg-[#00b67a] h-3 rounded-full transition-all duration-500"
                    style={{ width: `${oneJob.to_create ? Math.min(100, Math.round(((oneJob.created || 0) / oneJob.to_create) * 100)) : 0}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-3">Total visé : {oneJob.target}. Tu peux fermer, la génération continue côté serveur.</p>
                <div className="flex justify-center mt-4">
                  <button onClick={stopOne} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium" data-testid="generate-one-stop">
                    <X className="w-4 h-4" /> Arrêter la génération
                  </button>
                </div>
              </div>
            ) : (oneJob?.status === 'done' || oneJob?.status === 'stopped') ? (
              <div className="text-center py-4" data-testid="generate-one-done">
                <Check className="w-10 h-10 text-[#00b67a] mx-auto mb-2" />
                <p className="font-medium text-[#1a1a1a]">{oneJob.status === 'stopped' ? 'Génération arrêtée' : 'Terminé !'}</p>
                <p className="text-sm text-gray-600 mt-1">Total actuel : <strong>{oneJob.current_total}</strong> avis.</p>
                <button onClick={() => { setOneJob(null); openOneModal(oneCompany); }} className="mt-4 text-sm text-[#00b67a] hover:underline" data-testid="generate-one-again">
                  Générer davantage
                </button>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre total d'avis souhaité</label>
                <input type="number" value={oneTarget} onChange={(e) => setOneTarget(e.target.value)} min={oneCurrent + 1}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]" data-testid="generate-one-target-input" />
                <p className="text-xs text-gray-500 mt-1">
                  {oneTarget && parseInt(oneTarget, 10) > oneCurrent
                    ? `${parseInt(oneTarget, 10) - oneCurrent} nouveaux avis seront générés.`
                    : 'Doit être supérieur au total actuel.'}
                </p>
                {oneError && <p className="text-sm text-red-600 mt-2" data-testid="generate-one-error">{oneError}</p>}
                <DistributionFields
                  ratingPreset={oneRating} setRatingPreset={setOneRating}
                  dateFrom={oneFrom} setDateFrom={setOneFrom}
                  dateTo={oneTo} setDateTo={setOneTo}
                  idPrefix="one-"
                />
                <EstimationPanel
                  count={oneTarget && parseInt(oneTarget, 10) > oneCurrent ? parseInt(oneTarget, 10) - oneCurrent : 0}
                  idPrefix="one-"
                />

                {/* Aperçu de langue */}
                <div className="mt-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-gray-600">
                      Aperçu d'un avis en {(REVIEW_LANGUAGES.find((l) => l.value === (oneCompany.review_language || 'fr')) || {}).flag}{' '}
                      {(REVIEW_LANGUAGES.find((l) => l.value === (oneCompany.review_language || 'fr')) || {}).label}
                    </span>
                    <button
                      onClick={previewOne}
                      disabled={onePreviewLoading}
                      className="flex items-center gap-1 px-3 py-1.5 border border-[#00b67a] text-[#00b67a] rounded-lg hover:bg-[#00b67a]/10 disabled:opacity-50 transition-colors text-xs font-medium"
                      data-testid="generate-one-preview-button"
                    >
                      {onePreviewLoading
                        ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Génération…</>
                        : <><Sparkles className="w-3.5 h-3.5" /> Aperçu</>}
                    </button>
                  </div>
                  {onePreview && (
                    <div className="mt-3 bg-white rounded-lg border p-3" data-testid="generate-one-preview">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-[#1a1a1a]" data-testid="preview-author">{onePreview.author}</span>
                        <span className="text-sm" data-testid="preview-rating">{'★'.repeat(onePreview.rating)}<span className="text-gray-300">{'★'.repeat(5 - onePreview.rating)}</span></span>
                      </div>
                      <p className="text-sm font-medium text-[#1a1a1a]" data-testid="preview-title">{onePreview.title}</p>
                      <p className="text-sm text-gray-600 mt-1" data-testid="preview-content">{onePreview.content}</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button onClick={() => { setOneCompany(null); setOneJob(null); }} className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 transition-colors" data-testid="generate-one-cancel">Annuler</button>
                  <button onClick={startOne} disabled={oneStarting} className="flex items-center gap-2 px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] disabled:opacity-50 transition-colors font-medium" data-testid="generate-one-start">
                    <Sparkles className="w-4 h-4" />
                    {oneStarting ? 'Démarrage...' : 'Lancer la génération'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminDashboardPage;
